import { Controller, useFormContext } from 'react-hook-form';
import { Divider, Input } from '@ds';
import { CCSchedulerSettingsRulesFormType } from '@lp/features/Scheduler/SchedulerClinicalCoordinatorsSettings/types';
import { CCSettingsCustomRulesListForm } from '@lp/features/Scheduler/SchedulerClinicalCoordinatorsSettings/components/CCSettingsCustomRulesListForm';
import { getNumberInputValue, getNumberInputValueHandler } from '@lp/features/Scheduler/utils';
import { CCResource } from '@/api/apis/resources/schedule';

type CCSchedulerSettingsRulesFormProps = {
  userList: CCResource[];
};

export const CCSchedulerSettingsRulesForm = ({ userList }: CCSchedulerSettingsRulesFormProps) => {
  const { control } = useFormContext<CCSchedulerSettingsRulesFormType>();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 w-full p-6 bg-white rounded-2xl shadow-s">
        <span className="text-base-5 text-xl-sb">Individual Workload Requirements</span>

        <div className="flex flex-col gap-4">
          <span className="text-base-5 text-l-m">Shifts per Month</span>

          <div className="flex gap-12">
            <div>
              <span className="text-base-25 text-m-r">On Call TCI / TCII</span>
              <div className="flex gap-4">
                <Controller
                  name="tcOneTwoRules.minOnCall"
                  control={control}
                  render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
                    <Input
                      type="number"
                      label="Min"
                      min={0}
                      isInvalid={invalid}
                      errorMessage={error?.message}
                      className="w-[120px]"
                      value={getNumberInputValue(value)}
                      onValueChange={getNumberInputValueHandler(onChange)}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="tcOneTwoRules.maxOnCall"
                  control={control}
                  render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
                    <Input
                      type="number"
                      label="Max"
                      min={0}
                      isInvalid={invalid}
                      errorMessage={error?.message}
                      className="w-[120px]"
                      value={getNumberInputValue(value)}
                      onValueChange={getNumberInputValueHandler(onChange)}
                      {...field}
                    />
                  )}
                />
              </div>
            </div>

            <div>
              <span className="text-base-25 text-m-r">On Call TCIII</span>
              <div className="flex gap-4">
                <Controller
                  name="tcThreeRules.minOnCall"
                  control={control}
                  render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
                    <Input
                      type="number"
                      label="Min"
                      min={0}
                      isInvalid={invalid}
                      errorMessage={error?.message}
                      className="w-[120px]"
                      value={getNumberInputValue(value)}
                      onValueChange={getNumberInputValueHandler(onChange)}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="tcThreeRules.maxOnCall"
                  control={control}
                  render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
                    <Input
                      type="number"
                      label="Max"
                      min={0}
                      isInvalid={invalid}
                      errorMessage={error?.message}
                      className="w-[120px]"
                      value={getNumberInputValue(value)}
                      onValueChange={getNumberInputValueHandler(onChange)}
                      {...field}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </div>

        <Divider />

        <div className="flex flex-col gap-4">
          <span className="text-base-5 text-l-m">Weekend (Fri/Sat/Sun) Off per Month</span>
          <Controller
            name="weekendCount"
            control={control}
            render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
              <Input
                type="number"
                min={0}
                isInvalid={invalid}
                errorMessage={error?.message}
                className="w-[80px]"
                value={getNumberInputValue(value)}
                onValueChange={getNumberInputValueHandler(onChange)}
                {...field}
              />
            )}
          />
        </div>
      </div>

      <div className="flex flex-col gap-4 w-full p-6 bg-white rounded-2xl shadow-s">
        <span className="text-base-5 text-xl-sb">Individual Workload Guardrails</span>

        <div className="flex flex-col gap-4">
          <span className="text-base-5 text-l-m">Vacation Days</span>

          <div className="flex items-center gap-2 text-base-5 text-m-r">
            <span>Each</span>
            <Controller
              name="deductionRule.each"
              control={control}
              render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
                <Input
                  type="number"
                  min={0}
                  isInvalid={invalid}
                  errorMessage={error?.message}
                  className="w-[80px]"
                  value={getNumberInputValue(value)}
                  onValueChange={getNumberInputValueHandler(onChange)}
                  {...field}
                />
              )}
            />
            <span>vacation/personal days taken in any given month in reduce on-call requirement by</span>
            <Controller
              name="deductionRule.deduction"
              control={control}
              render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
                <Input
                  type="number"
                  min={0}
                  isInvalid={invalid}
                  errorMessage={error?.message}
                  className="w-[80px]"
                  value={getNumberInputValue(value)}
                  onValueChange={getNumberInputValueHandler(onChange)}
                  {...field}
                />
              )}
            />
            <span>day for that month.</span>
          </div>
        </div>
      </div>

      <CCSettingsCustomRulesListForm userList={userList} />
    </div>
  );
};
